/**
 * @module @theme/wishlist
 *
 * Wishlist storage layer for the Kiss of Aroma theme.
 *
 * Persists a list of product handles to localStorage and broadcasts
 * `wishlist:update` events on `document` whenever the list changes —
 * including when another tab edits the same wishlist (cross-tab sync
 * via the native `storage` event).
 *
 * Public API:
 *   import { wishlist, WishlistEvents } from '@theme/wishlist';
 *   wishlist.add(handle, productId?);
 *   wishlist.remove(handle);
 *   wishlist.toggle(handle, productId?);
 *   wishlist.has(handle);
 *   wishlist.count();
 *   wishlist.items();
 *   wishlist.clear();
 *   wishlist.buildShareUrl();
 *   wishlist.importFromUrl();
 *
 *   document.addEventListener(WishlistEvents.update, (e) => {
 *     // e.detail = { items, action, handle, count }
 *   });
 */

const STORAGE_KEY = 'koa:wishlist';
const SHARE_PARAM = 'w';
const MAX_ITEMS = 200;
const WISHLIST_PATH = '/pages/wishlist';

/**
 * @typedef {Object} WishlistItem
 * @property {string} handle  - The product handle.
 * @property {number|null} id - The product id (optional, when known).
 * @property {number} t       - Timestamp when added (ms since epoch).
 */

export const WishlistEvents = Object.freeze({
  update: 'wishlist:update',
});

/**
 * Event fired when the wishlist contents change.
 */
export class WishlistUpdateEvent extends Event {
  /**
   * @param {Object} detail
   * @param {WishlistItem[]} detail.items
   * @param {'add'|'remove'|'clear'|'sync'|'import'|'init'} detail.action
   * @param {string|null} [detail.handle]
   * @param {number} detail.count
   */
  constructor(detail) {
    super(WishlistEvents.update, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * Singleton wishlist controller.
 */
class Wishlist {
  /** @type {WishlistItem[]} */
  #cache = this.#read();

  constructor() {
    /* Cross-tab synchronisation */
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#cache = this.#read();
      this.#emit('sync', null);
    });

    /* Auto-import shared wishlist on first load (if URL has ?w=...) */
    queueMicrotask(() => {
      try {
        this.importFromUrl();
      } catch (error) {
        /* never block the page */
        console.warn('[wishlist] importFromUrl failed:', error);
      }
      /* Always emit an init event so listeners can paint counts */
      this.#emit('init', null);
    });
  }

  /* ── Public API ───────────────────────────────────────────── */

  /** @returns {WishlistItem[]} A defensive copy of the items list. */
  items() {
    return this.#cache.slice();
  }

  /** @returns {number} Number of saved items. */
  count() {
    return this.#cache.length;
  }

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  has(handle) {
    if (!handle) return false;
    return this.#cache.some((item) => item.handle === handle);
  }

  /**
   * Adds a product to the wishlist. Idempotent — duplicates are ignored.
   * @param {string} handle
   * @param {number|string|null} [productId]
   * @returns {boolean} true if a new item was added.
   */
  add(handle, productId = null) {
    if (!handle) return false;
    if (this.has(handle)) return false;

    const next = this.#cache.slice();
    next.unshift({
      handle: String(handle),
      id: productId == null ? null : Number(productId) || null,
      t: Date.now(),
    });

    this.#commit(next);
    this.#emit('add', handle);
    return true;
  }

  /**
   * Removes a product from the wishlist.
   * @param {string} handle
   * @returns {boolean} true if an item was removed.
   */
  remove(handle) {
    if (!handle) return false;
    const next = this.#cache.filter((item) => item.handle !== handle);
    if (next.length === this.#cache.length) return false;

    this.#commit(next);
    this.#emit('remove', handle);
    return true;
  }

  /**
   * Toggles a product's presence in the wishlist.
   * @param {string} handle
   * @param {number|string|null} [productId]
   * @returns {boolean} The new presence state (true = in wishlist after the toggle).
   */
  toggle(handle, productId = null) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle, productId);
    return true;
  }

  /** Removes every item from the wishlist. */
  clear() {
    if (this.#cache.length === 0) return;
    this.#commit([]);
    this.#emit('clear', null);
  }

  /**
   * Builds a shareable URL containing the current handles.
   * @returns {string|null} Absolute URL, or null when the wishlist is empty.
   */
  buildShareUrl() {
    const handles = this.#cache.map((item) => item.handle);
    if (handles.length === 0) return null;
    const token = encodeURIComponent(handles.join(','));
    const origin = window.location.origin;
    const path = (window.Theme && window.Theme.routes && window.Theme.routes.wishlist_url) || WISHLIST_PATH;
    return `${origin}${path}?${SHARE_PARAM}=${token}`;
  }

  /**
   * If the current URL contains `?w=handle1,handle2`, merge those handles
   * into the local wishlist (deduping) and then strip the param from the URL
   * so refreshes don't re-import.
   * @returns {number} The count of newly-added items.
   */
  importFromUrl() {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(SHARE_PARAM);
    if (!raw) return 0;

    const incoming = decodeURIComponent(raw)
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);

    if (incoming.length === 0) {
      url.searchParams.delete(SHARE_PARAM);
      window.history.replaceState({}, '', url.toString());
      return 0;
    }

    const existing = new Set(this.#cache.map((item) => item.handle));
    const next = this.#cache.slice();
    let added = 0;
    for (const handle of incoming) {
      if (existing.has(handle)) continue;
      next.unshift({ handle, id: null, t: Date.now() });
      existing.add(handle);
      added += 1;
    }

    if (added > 0) {
      this.#commit(next);
      this.#emit('import', null);
    }

    /* Strip the share param from the URL — don't re-import on reload. */
    url.searchParams.delete(SHARE_PARAM);
    const search = url.searchParams.toString();
    const cleanUrl = url.pathname + (search ? `?${search}` : '') + url.hash;
    window.history.replaceState({}, '', cleanUrl);
    return added;
  }

  /* ── Internals ────────────────────────────────────────────── */

  #read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (item) => item && typeof item.handle === 'string' && item.handle.length > 0
      );
    } catch (_error) {
      return [];
    }
  }

  /** @param {WishlistItem[]} next */
  #commit(next) {
    const trimmed = next.slice(0, MAX_ITEMS);
    this.#cache = trimmed;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch (_error) {
      /* localStorage unavailable (private mode, quota): degrade silently. */
    }
  }

  /**
   * @param {'add'|'remove'|'clear'|'sync'|'import'|'init'} action
   * @param {string|null} handle
   */
  #emit(action, handle) {
    document.dispatchEvent(
      new WishlistUpdateEvent({
        items: this.items(),
        action,
        handle,
        count: this.#cache.length,
      })
    );
  }
}

/* ── Singleton ──────────────────────────────────────────────── */

export const wishlist = new Wishlist();

/* Also expose on `window` for non-module callers (theme editor, custom liquid blocks). */
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).Wishlist = wishlist;
}
